import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { API, setAuthToken } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Loader2, Download, User, Building2, Clock, CheckCircle2, ArrowRight, Briefcase, Users, BarChart3, ArrowLeft, Mail, KeyRound, ShieldCheck } from 'lucide-react';

const Login = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('candidate');
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstall, setShowInstall] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  
  // Forgot Password State
  const [forgotPasswordStep, setForgotPasswordStep] = useState(0); // 0: hidden, 1: enter email, 2: enter code, 3: new password
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  
  // Referral Code State
  const [showReferralInput, setShowReferralInput] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [referralVerified, setReferralVerified] = useState(null);
  const [referralLoading, setReferralLoading] = useState(false);
  
  // Carousel slides
  const slides = [
    {
      image: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600&q=80",
      title: "Seamless work experience",
      subtitle: "Everything you need in an easily customizable dashboard"
    },
    {
      image: "https://images.unsplash.com/photo-1553877522-43269d4ea984?w=600&q=80", 
      title: "Connect with top talent",
      subtitle: "Find verified candidates for your organization"
    },
    {
      image: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=600&q=80",
      title: "Grow your career",
      subtitle: "Discover opportunities that match your skills"
    }
  ];

  // Auto-rotate slides
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);
  
  // Candidate form data
  const [candidateFormData, setCandidateFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    city: '',
    role: 'candidate'
  });

  // Employer form data
  const [employerFormData, setEmployerFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    company_name: '',
    role: 'employer'
  });

  useEffect(() => {
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstall(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    if (window.matchMedia('(display-mode: standalone)').matches) {
      setShowInstall(false);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      toast.info('App can be installed from browser menu');
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      toast.success('App installed successfully!');
      setShowInstall(false);
    }
    setDeferredPrompt(null);
  };

  // Verify Referral Code
  const handleVerifyReferralCode = async () => {
    if (!referralCode.trim()) {
      toast.error('Please enter a referral code');
      return;
    }
    
    setReferralLoading(true);
    try {
      const response = await axios.post(`${API}/auth/verify-referral-code`, {
        code: referralCode.trim()
      });
      
      setReferralVerified(response.data);
      
      if (response.data.already_used) {
        toast.info('This referral code has already been used');
      } else {
        toast.success(`Referral verified! You were referred for ${response.data.referral.job_title} at ${response.data.referral.company_name}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid referral code');
      setReferralVerified(null);
    } finally {
      setReferralLoading(false);
    }
  };

  const handleCandidateSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin
        ? { email: candidateFormData.email, password: candidateFormData.password }
        : candidateFormData;

      const response = await axios.post(`${API}${endpoint}`, payload);
      
      if (isLogin) {
        setAuthToken(response.data.token);
        toast.success('Login successful!');
        
        const userRole = response.data.role;
        if (userRole === 'candidate') {
          navigate('/candidate');
        } else if (userRole === 'lead_specialist') {
          navigate('/lead-specialist');
        } else {
          navigate('/dashboard');
        }
      } else {
        if (response.data.token) {
          setAuthToken(response.data.token);
          toast.success('Account created! Welcome to Officium Connect.');
          navigate('/candidate');
        } else {
          toast.success('Registration successful! You can now login.');
          setIsLogin(true);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleEmployerSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin
        ? { email: employerFormData.email, password: employerFormData.password }
        : employerFormData;

      const response = await axios.post(`${API}${endpoint}`, payload);
      
      if (isLogin) {
        if (response.data.approval_status === 'pending') {
          toast.info('Your employer account is pending admin approval. Please wait for approval.');
          setLoading(false);
          return;
        }
        
        setAuthToken(response.data.token);
        toast.success('Login successful!');
        
        // Admin users go to main dashboard, regular employers go to employer dashboard
        if (response.data.user?.is_admin) {
          navigate('/dashboard');
        } else {
          navigate('/employer-dashboard');
        }
      } else {
        toast.success('Registration submitted! Please wait for admin approval before you can login and post jobs.');
        setIsLogin(true);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'An error occurred';
      if (errorMsg.includes('pending admin approval')) {
        toast.info('Your employer account is pending admin approval. Please wait for approval.');
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  // Forgot Password Handlers
  const handleForgotPassword = () => {
    setForgotPasswordStep(1);
    setForgotEmail('');
    setResetCode('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleSendResetCode = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error('Please enter your email');
      return;
    }
    
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      toast.success('Reset code sent to your email!');
      setForgotPasswordStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset code');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (!resetCode || resetCode.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }
    
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/verify-reset-code`, { 
        email: forgotEmail, 
        code: resetCode 
      });
      toast.success('Code verified! Set your new password.');
      setForgotPasswordStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid or expired code');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        email: forgotEmail,
        code: resetCode,
        new_password: newPassword
      });
      toast.success('Password reset successfully! You can now login.');
      setForgotPasswordStep(0);
      setIsLogin(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setForgotPasswordStep(0);
    setForgotEmail('');
    setResetCode('');
    setNewPassword('');
    setConfirmPassword('');
  };

  return (
    <div className="min-h-screen flex bg-white">
      {/* Left Side - Illustration Panel - Navy SaaS Style */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#0F4C81] relative overflow-hidden">
        {/* Subtle Gradient Mesh Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(16,185,129,0.15)_0%,transparent_50%)]"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(255,255,255,0.08)_0%,transparent_40%)]"></div>
        </div>
        
        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: '40px 40px'
        }}></div>

        {/* Content */}
        <div className="relative z-10 flex flex-col items-center justify-center w-full p-12">
          {/* Glass Card */}
          <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 max-w-lg w-full border border-white/20 shadow-2xl">
            {/* Image */}
            <div className="relative h-64 mb-6 rounded-xl overflow-hidden">
              <img 
                src={slides[activeSlide].image}
                alt="Work illustration"
                className="w-full h-full object-cover transition-opacity duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F4C81]/60 to-transparent"></div>
            </div>

            {/* Text */}
            <h2 className="font-heading text-2xl font-bold text-white mb-2 text-center tracking-tight">
              {slides[activeSlide].title}
            </h2>
            <p className="text-white/80 text-center leading-relaxed">
              {slides[activeSlide].subtitle}
            </p>

            {/* Pagination Dots */}
            <div className="flex items-center justify-center gap-2 mt-6">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveSlide(index)}
                  className={`transition-all duration-300 rounded-full ${
                    index === activeSlide 
                      ? 'w-8 h-2 bg-[#10B981]' 
                      : 'w-2 h-2 bg-white/30 hover:bg-white/50'
                  }`}
                  data-testid={`slide-dot-${index}`}
                />
              ))}
            </div>
          </div>

          {/* Stats */}
          <div className="flex items-center gap-8 mt-10">
            <div className="text-center">
              <div className="flex items-center justify-center w-14 h-14 rounded-xl bg-white/10 backdrop-blur-sm border border-white/10 mb-3 mx-auto">
                <Briefcase className="h-6 w-6 text-[#10B981]" />
              </div>
              <p className="font-heading text-2xl font-bold text-white">1000+</p>
              <p className="text-sm text-white/60 mt-1">Active Jobs</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center w-14 h-14 rounded-xl bg-white/10 backdrop-blur-sm border border-white/10 mb-3 mx-auto">
                <Users className="h-6 w-6 text-[#10B981]" />
              </div>
              <p className="font-heading text-2xl font-bold text-white">1500+</p>
              <p className="text-sm text-white/60 mt-1">Candidates</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center w-14 h-14 rounded-xl bg-white/10 backdrop-blur-sm border border-white/10 mb-3 mx-auto">
                <BarChart3 className="h-6 w-6 text-[#10B981]" />
              </div>
              <p className="font-heading text-2xl font-bold text-white">98%</p>
              <p className="text-sm text-white/60 mt-1">Success Rate</p>
            </div>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-[#10B981]/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-white/5 rounded-full blur-3xl"></div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-6 md:px-12 lg:px-16 py-12 bg-slate-50">
        <div className="max-w-md mx-auto w-full">
          {/* Logo */}
          <div className="mb-10">
            <Link to="/" className="inline-flex items-center gap-3 group">
              <img 
                src="/logo.png?v=15" 
                alt="Officium Connect" 
                className="h-12 transition-transform duration-300 group-hover:scale-105"
                data-testid="company-logo"
              />
              <span className="font-heading text-xl font-bold text-slate-900 tracking-tight">Officium Connect</span>
            </Link>
          </div>

          {/* Welcome Text */}
          <div className="mb-8">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 mb-2 tracking-tight">
              {forgotPasswordStep === 0 ? 'Welcome Back' : 
               forgotPasswordStep === 1 ? 'Forgot Password' :
               forgotPasswordStep === 2 ? 'Enter Verification Code' :
               'Create New Password'}
            </h1>
            <p className="text-slate-500 leading-relaxed">
              {forgotPasswordStep === 0 ? 'Please enter your details to continue' :
               forgotPasswordStep === 1 ? 'Enter your email to receive a reset code' :
               forgotPasswordStep === 2 ? 'Check your email for the 6-digit code' :
               'Set a strong password for your account'}
            </p>
          </div>

          {/* Forgot Password Flow */}
          {forgotPasswordStep > 0 ? (
            <div className="space-y-6">
              {/* Step 1: Enter Email */}
              {forgotPasswordStep === 1 && (
                <form onSubmit={handleSendResetCode} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="forgot-email" className="text-slate-700 font-medium">Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                      <Input
                        id="forgot-email"
                        type="email"
                        required
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        className="h-12 pl-12 rounded-xl border-slate-200 bg-white focus:border-[#0F4C81] focus:ring-[#0F4C81] transition-colors"
                        placeholder="you@example.com"
                        data-testid="forgot-email-input"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 rounded-xl font-medium bg-[#0F4C81] hover:bg-[#0A3459] text-white flex items-center justify-center gap-2"
                    disabled={forgotLoading}
                    data-testid="send-code-btn"
                  >
                    {forgotLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Reset Code
                        <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </Button>
                </form>
              )}

              {/* Step 2: Enter Code */}
              {forgotPasswordStep === 2 && (
                <form onSubmit={handleVerifyCode} className="space-y-5">
                  <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 flex items-start gap-3">
                    <Mail className="h-5 w-5 text-[#0F4C81] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-indigo-800">Code sent to {forgotEmail}</p>
                      <p className="text-xs text-[#0F4C81] mt-1">The code expires in 15 minutes</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="reset-code" className="text-slate-700 font-medium">Verification Code</Label>
                    <div className="relative">
                      <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                      <Input
                        id="reset-code"
                        type="text"
                        required
                        maxLength={6}
                        value={resetCode}
                        onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        className="h-12 pl-12 rounded-xl border-slate-200 focus:border-[#0F4C81] focus:ring-[#0F4C81] text-center text-2xl tracking-widest font-mono"
                        placeholder="000000"
                        data-testid="reset-code-input"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 rounded-xl font-medium bg-[#0F4C81] hover:bg-[#0A3459] text-white flex items-center justify-center gap-2"
                    disabled={forgotLoading || resetCode.length !== 6}
                    data-testid="verify-code-btn"
                  >
                    {forgotLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      <>
                        Verify Code
                        <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </Button>

                  <button
                    type="button"
                    onClick={() => setForgotPasswordStep(1)}
                    className="w-full text-sm text-[#0F4C81] hover:text-[#0A3459] font-medium"
                  >
                    Didn't receive code? Send again
                  </button>
                </form>
              )}

              {/* Step 3: New Password */}
              {forgotPasswordStep === 3 && (
                <form onSubmit={handleResetPassword} className="space-y-5">
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-3">
                    <ShieldCheck className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-green-800">Email verified successfully!</p>
                      <p className="text-xs text-green-600 mt-1">You can now create your new password</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="new-password" className="text-slate-700 font-medium">New Password</Label>
                    <Input
                      id="new-password"
                      type="password"
                      required
                      minLength={6}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="h-12 rounded-xl border-slate-200 focus:border-[#0F4C81] focus:ring-[#0F4C81]"
                      placeholder="••••••••"
                      data-testid="new-password-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirm-password" className="text-slate-700 font-medium">Confirm Password</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      required
                      minLength={6}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="h-12 rounded-xl border-slate-200 focus:border-[#0F4C81] focus:ring-[#0F4C81]"
                      placeholder="••••••••"
                      data-testid="confirm-password-input"
                    />
                    {confirmPassword && newPassword !== confirmPassword && (
                      <p className="text-xs text-red-500">Passwords do not match</p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 rounded-xl font-medium bg-[#0F4C81] hover:bg-[#0A3459] text-white flex items-center justify-center gap-2"
                    disabled={forgotLoading || !newPassword || newPassword !== confirmPassword}
                    data-testid="reset-password-btn"
                  >
                    {forgotLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Resetting...
                      </>
                    ) : (
                      <>
                        Reset Password
                        <CheckCircle2 className="h-4 w-4" />
                      </>
                    )}
                  </Button>
                </form>
              )}

              {/* Back to Login */}
              <button
                type="button"
                onClick={handleBackToLogin}
                className="w-full flex items-center justify-center gap-2 text-sm text-slate-600 hover:text-slate-800"
                data-testid="back-to-login-btn"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Login
              </button>
            </div>
          ) : (
          <>
          {/* User Type Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full mb-6">
            <TabsList className="grid w-full grid-cols-2 h-14 p-1.5 bg-white rounded-xl border border-slate-200 shadow-sm" data-testid="login-tabs">
              <TabsTrigger 
                value="candidate" 
                className="rounded-lg data-[state=active]:bg-[#0F4C81] data-[state=active]:text-white data-[state=active]:shadow-md flex items-center gap-2 font-medium transition-all duration-200"
                data-testid="candidate-tab"
              >
                <User className="h-4 w-4" />
                Candidate
              </TabsTrigger>
              <TabsTrigger 
                value="employer" 
                className="rounded-lg data-[state=active]:bg-[#0F4C81] data-[state=active]:text-white data-[state=active]:shadow-md flex items-center gap-2 font-medium transition-all duration-200"
                data-testid="employer-tab"
              >
                <Building2 className="h-4 w-4" />
                Employer
              </TabsTrigger>
            </TabsList>

            {/* Candidate Tab */}
            <TabsContent value="candidate" className="mt-6">
              <form onSubmit={handleCandidateSubmit} className="space-y-5">
                {!isLogin && (
                  <div className="space-y-2">
                    <Label htmlFor="candidate-name" className="text-slate-700 font-medium">Full Name</Label>
                    <Input
                      id="candidate-name"
                      data-testid="candidate-name-input"
                      type="text"
                      required
                      value={candidateFormData.name}
                      onChange={(e) => setCandidateFormData({ ...candidateFormData, name: e.target.value })}
                      className="h-12 rounded-xl border-slate-200 focus:border-[#0F4C81] focus:ring-[#0F4C81]"
                      placeholder="John Doe"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="candidate-email" className="text-slate-700 font-medium">Email Address</Label>
                  <Input
                    id="candidate-email"
                    data-testid="candidate-email-input"
                    type="email"
                    required
                    value={candidateFormData.email}
                    onChange={(e) => setCandidateFormData({ ...candidateFormData, email: e.target.value })}
                    className="h-12 rounded-xl border-slate-200 focus:border-[#0F4C81] focus:ring-[#0F4C81]"
                    placeholder="you@example.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="candidate-password" className="text-slate-700 font-medium">Password</Label>
                  <Input
                    id="candidate-password"
                    data-testid="candidate-password-input"
                    type="password"
                    required
                    value={candidateFormData.password}
                    onChange={(e) => setCandidateFormData({ ...candidateFormData, password: e.target.value })}
                    className="h-12 rounded-xl border-slate-200 focus:border-[#0F4C81] focus:ring-[#0F4C81]"
                    placeholder="••••••••"
                  />
                </div>

                {!isLogin && (
                  <div className="space-y-2">
                    <Label htmlFor="candidate-phone" className="text-slate-700 font-medium">Phone Number</Label>
                    <Input
                      id="candidate-phone"
                      data-testid="candidate-phone-input"
                      type="tel"
                      required
                      placeholder="+91 9876543210"
                      value={candidateFormData.phone}
                      onChange={(e) => setCandidateFormData({ ...candidateFormData, phone: e.target.value })}
                      className="h-12 rounded-xl border-slate-200 focus:border-[#0F4C81] focus:ring-[#0F4C81]"
                    />
                  </div>
                )}

                {!isLogin && (
                  <div className="space-y-2">
                    <Label htmlFor="candidate-city" className="text-slate-700 font-medium">City</Label>
                    <Select 
                      value={candidateFormData.city} 
                      onValueChange={(value) => setCandidateFormData({ ...candidateFormData, city: value })}
                    >
                      <SelectTrigger className="h-12 rounded-xl border-slate-200 focus:border-[#0F4C81] focus:ring-[#0F4C81]" data-testid="candidate-city-select">
                        <SelectValue placeholder="Select your city" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Mumbai">Mumbai</SelectItem>
                        <SelectItem value="Delhi">Delhi</SelectItem>
                        <SelectItem value="Bangalore">Bangalore</SelectItem>
                        <SelectItem value="Hyderabad">Hyderabad</SelectItem>
                        <SelectItem value="Chennai">Chennai</SelectItem>
                        <SelectItem value="Kolkata">Kolkata</SelectItem>
                        <SelectItem value="Pune">Pune</SelectItem>
                        <SelectItem value="Ahmedabad">Ahmedabad</SelectItem>
                        <SelectItem value="Jaipur">Jaipur</SelectItem>
                        <SelectItem value="Lucknow">Lucknow</SelectItem>
                        <SelectItem value="Noida">Noida</SelectItem>
                        <SelectItem value="Gurgaon">Gurgaon</SelectItem>
                        <SelectItem value="Chandigarh">Chandigarh</SelectItem>
                        <SelectItem value="Indore">Indore</SelectItem>
                        <SelectItem value="Bhopal">Bhopal</SelectItem>
                        <SelectItem value="Nagpur">Nagpur</SelectItem>
                        <SelectItem value="Surat">Surat</SelectItem>
                        <SelectItem value="Kochi">Kochi</SelectItem>
                        <SelectItem value="Coimbatore">Coimbatore</SelectItem>
                        <SelectItem value="Visakhapatnam">Visakhapatnam</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {isLogin && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Checkbox 
                        id="remember" 
                        checked={rememberMe}
                        onCheckedChange={setRememberMe}
                        className="border-gray-300"
                      />
                      <label htmlFor="remember" className="text-sm text-slate-600 cursor-pointer">
                        Remember me
                      </label>
                    </div>
                    <button 
                      type="button" 
                      onClick={handleForgotPassword}
                      className="text-sm text-[#0F4C81] hover:text-[#0A3459] font-medium"
                      data-testid="forgot-password-btn"
                    >
                      Forgot Password?
                    </button>
                  </div>
                )}

                {/* Referral Code Section - Optional */}
                {isLogin && (
                  <div className="space-y-2">
                    <Label htmlFor="referral-code" className="text-slate-700 font-medium flex items-center gap-2">
                      Referral Code
                      <span className="text-xs text-slate-400 font-normal">(Optional)</span>
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        id="referral-code"
                        type="text"
                        value={referralCode}
                        onChange={(e) => {
                          setReferralCode(e.target.value.toUpperCase());
                          setReferralVerified(null);
                        }}
                        placeholder="Enter code if you have one"
                        className="h-12 rounded-xl border-slate-200 focus:border-[#0F4C81] focus:ring-[#0F4C81] uppercase tracking-wider font-mono"
                        maxLength={8}
                        data-testid="referral-code-input"
                      />
                      {referralCode.trim() && (
                        <Button
                          type="button"
                          onClick={handleVerifyReferralCode}
                          disabled={referralLoading}
                          className="px-4 h-12 bg-[#0F4C81] hover:bg-[#0A3459] text-white rounded-xl"
                          data-testid="verify-referral-btn"
                        >
                          {referralLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verify'}
                        </Button>
                      )}
                    </div>
                    
                    {referralVerified && (
                      <div className={`p-3 rounded-xl ${referralVerified.already_used ? 'bg-amber-50 border border-amber-200' : 'bg-green-50 border border-green-200'}`}>
                        <div className="flex items-start gap-2">
                          <CheckCircle2 className={`h-5 w-5 ${referralVerified.already_used ? 'text-amber-600' : 'text-green-600'} flex-shrink-0 mt-0.5`} />
                          <div>
                            <p className={`text-sm font-medium ${referralVerified.already_used ? 'text-amber-800' : 'text-green-800'}`}>
                              {referralVerified.already_used ? 'Code Already Used' : 'Referral Verified!'}
                            </p>
                            <p className="text-xs text-slate-600 mt-1">
                              {referralVerified.referral.job_title} at {referralVerified.referral.company_name}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <Button
                  type="submit"
                  data-testid="candidate-submit-button"
                  className="w-full h-12 rounded-xl font-medium bg-[#0F4C81] hover:bg-[#0A3459] text-white flex items-center justify-center gap-2 transition-all"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Please wait
                    </>
                  ) : (
                    <>
                      {isLogin ? 'Login' : 'Create Account'}
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </Button>
              </form>

              {/* Terms */}
              <p className="text-xs text-slate-500 mt-6">
                By creating an account, you agree to our{' '}
                <Link to="/terms" className="text-[#0F4C81] hover:underline">Terms of Service</Link>
                {' '}and{' '}
                <Link to="/privacy" className="text-[#0F4C81] hover:underline">Privacy Policy</Link>
              </p>

              {/* Toggle Auth */}
              <p className="text-center text-sm text-slate-600 mt-6">
                {isLogin ? "Don't have an account?" : 'Already have an account?'}{' '}
                <button
                  type="button"
                  data-testid="candidate-toggle-auth"
                  onClick={() => setIsLogin(!isLogin)}
                  className="text-[#0F4C81] hover:text-[#0A3459] font-semibold"
                >
                  {isLogin ? 'Sign Up' : 'Sign In'}
                </button>
              </p>
            </TabsContent>

            {/* Employer Tab */}
            <TabsContent value="employer" className="mt-6">
              {!isLogin && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3 mb-6">
                  <Clock className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Admin Approval Required</p>
                    <p className="text-xs text-amber-700 mt-1">
                      Employer accounts require admin approval before you can post jobs.
                    </p>
                  </div>
                </div>
              )}

              <form onSubmit={handleEmployerSubmit} className="space-y-5">
                {!isLogin && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="employer-name" className="text-slate-700 font-medium">Full Name</Label>
                      <Input
                        id="employer-name"
                        data-testid="employer-name-input"
                        type="text"
                        required
                        value={employerFormData.name}
                        onChange={(e) => setEmployerFormData({ ...employerFormData, name: e.target.value })}
                        className="h-12 rounded-xl border-slate-200 focus:border-[#0F4C81] focus:ring-[#0F4C81]"
                        placeholder="Your Name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="employer-company" className="text-slate-700 font-medium">Company Name</Label>
                      <Input
                        id="employer-company"
                        data-testid="employer-company-input"
                        type="text"
                        required
                        value={employerFormData.company_name}
                        onChange={(e) => setEmployerFormData({ ...employerFormData, company_name: e.target.value })}
                        className="h-12 rounded-xl border-slate-200 focus:border-[#0F4C81] focus:ring-[#0F4C81]"
                        placeholder="ABC Pvt Ltd"
                      />
                    </div>
                  </>
                )}

                <div className="space-y-2">
                  <Label htmlFor="employer-email" className="text-slate-700 font-medium">Business Email</Label>
                  <Input
                    id="employer-email"
                    data-testid="employer-email-input"
                    type="email"
                    required
                    value={employerFormData.email}
                    onChange={(e) => setEmployerFormData({ ...employerFormData, email: e.target.value })}
                    className="h-12 rounded-xl border-slate-200 focus:border-[#0F4C81] focus:ring-[#0F4C81]"
                    placeholder="hr@company.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="employer-password" className="text-slate-700 font-medium">Password</Label>
                  <Input
                    id="employer-password"
                    data-testid="employer-password-input"
                    type="password"
                    required
                    value={employerFormData.password}
                    onChange={(e) => setEmployerFormData({ ...employerFormData, password: e.target.value })}
                    className="h-12 rounded-xl border-slate-200 focus:border-[#0F4C81] focus:ring-[#0F4C81]"
                    placeholder="••••••••"
                  />
                </div>

                {!isLogin && (
                  <div className="space-y-2">
                    <Label htmlFor="employer-phone" className="text-slate-700 font-medium">Phone Number</Label>
                    <Input
                      id="employer-phone"
                      data-testid="employer-phone-input"
                      type="tel"
                      required
                      placeholder="+91 9876543210"
                      value={employerFormData.phone}
                      onChange={(e) => setEmployerFormData({ ...employerFormData, phone: e.target.value })}
                      className="h-12 rounded-xl border-slate-200 focus:border-[#0F4C81] focus:ring-[#0F4C81]"
                    />
                  </div>
                )}

                {isLogin && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Checkbox 
                        id="remember-employer" 
                        checked={rememberMe}
                        onCheckedChange={setRememberMe}
                        className="border-gray-300"
                      />
                      <label htmlFor="remember-employer" className="text-sm text-slate-600 cursor-pointer">
                        Remember me
                      </label>
                    </div>
                    <button 
                      type="button" 
                      onClick={handleForgotPassword}
                      className="text-sm text-[#0F4C81] hover:text-[#0A3459] font-medium"
                      data-testid="employer-forgot-password-btn"
                    >
                      Forgot Password?
                    </button>
                  </div>
                )}

                <Button
                  type="submit"
                  data-testid="employer-submit-button"
                  className="w-full h-12 rounded-xl font-medium bg-[#0F4C81] hover:bg-[#0A3459] text-white flex items-center justify-center gap-2 transition-all"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Please wait
                    </>
                  ) : (
                    <>
                      {isLogin ? 'Login' : 'Submit for Approval'}
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </Button>
              </form>

              {/* Employer Benefits */}
              {!isLogin && (
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <p className="text-sm font-medium text-slate-700 mb-3">After approval, you can:</p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      Post unlimited job openings
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      Receive candidate applications
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      Send selection/rejection emails
                    </div>
                  </div>
                </div>
              )}

              {/* Terms */}
              <p className="text-xs text-slate-500 mt-6">
                By creating an account, you agree to our{' '}
                <Link to="/terms" className="text-[#0F4C81] hover:underline">Terms of Service</Link>
                {' '}and{' '}
                <Link to="/privacy" className="text-[#0F4C81] hover:underline">Privacy Policy</Link>
              </p>

              {/* Toggle Auth */}
              <p className="text-center text-sm text-slate-600 mt-6">
                {isLogin ? "Don't have an account?" : 'Already have an account?'}{' '}
                <button
                  type="button"
                  data-testid="employer-toggle-auth"
                  onClick={() => setIsLogin(!isLogin)}
                  className="text-[#0F4C81] hover:text-[#0A3459] font-semibold"
                >
                  {isLogin ? 'Sign Up' : 'Sign In'}
                </button>
              </p>
            </TabsContent>
          </Tabs>

          {/* Install App Button */}
          {showInstall && (
            <div className="pt-6 border-t border-gray-100">
              <Button
                type="button"
                variant="outline"
                className="w-full h-12 rounded-xl border-slate-200"
                onClick={handleInstallClick}
                data-testid="install-app-button"
              >
                <Download className="h-4 w-4 mr-2" />
                Install App
              </Button>
            </div>
          )}
          </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
